import React from 'react';
import MergeTypeIcon from '@mui/icons-material/MergeType';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import WorkspacePremiumIcon from '@mui/icons-material/WorkspacePremium';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import VideocamIcon from '@mui/icons-material/Videocam';
import EventAvailableIcon from '@mui/icons-material/EventAvailable';
import QuestionAnswerIcon from '@mui/icons-material/QuestionAnswer';
import RocketLaunchIcon from '@mui/icons-material/RocketLaunch';
import SupportAgentIcon from '@mui/icons-material/SupportAgent';
import RecordVoiceOverIcon from '@mui/icons-material/RecordVoiceOver';
import ConfirmationNumberIcon from '@mui/icons-material/ConfirmationNumber';

// Illustrative mock-ups for the marketing pages. They are decorative
// (aria-hidden) and must never show real names, amounts or fake results.

const PrCard = () => (
  <div className="mv-card mv-pr">
    <span className="mv-icon mv-green"><MergeTypeIcon sx={{ fontSize: 18 }} /></span>
    <div>
      <strong>PR #214 approved</strong>
      <span>Senior engineer · “Clean work, ship it 🚀”</span>
    </div>
  </div>
);

const AiChat = () => (
  <div className="mv-card mv-ai">
    <div className="mv-ai-head"><AutoAwesomeIcon sx={{ fontSize: 16 }} /> AI pair programmer</div>
    <p className="mv-bubble mv-bubble-you">Add pagination to the orders API and write tests.</p>
    <p className="mv-bubble mv-bubble-ai">Here’s a cursor-based version with 6 tests. Review the edge case on line 42 ↓</p>
    <div className="mv-code">
      <span><i className="k">const</i> page = <i className="f">paginate</i>(orders, cursor);</span>
      <span><i className="c">{'// ✓ 6 tests passing'}</i></span>
    </div>
  </div>
);

const Letter = ({ title = 'Internship Letter' }) => (
  <div className="mv-paper mv-letter">
    <span className="mv-stamp">Sample</span>
    <div className="mv-paper-head">
      <span className="mv-logo">Codeazy</span>
      <WorkspacePremiumIcon sx={{ fontSize: 22 }} />
    </div>
    <strong>{title}</strong>
    <span className="mv-line" />
    <span className="mv-line" />
    <span className="mv-line short" />
    <div className="mv-sign">
      <span className="mv-line tiny" />
      <small>Engineering Lead</small>
    </div>
  </div>
);

const Payslip = () => (
  <div className="mv-paper mv-payslip">
    <span className="mv-stamp">Sample</span>
    <div className="mv-paper-head">
      <span className="mv-logo">Pay slip</span>
      <ReceiptLongIcon sx={{ fontSize: 20 }} />
    </div>
    <div className="mv-row"><span>Role</span><span>Engineer</span></div>
    <div className="mv-row"><span>Period</span><span>Month 1</span></div>
    <div className="mv-row total"><span>Net pay</span><span>₹ ••,•••</span></div>
  </div>
);

// Hero collage for pages selling the Real Engineering Team
export const TeamHeroVisual = ({ letter }) => (
  <div className="mv-collage" aria-hidden="true">
    <div className="mv-float mv-f1"><AiChat /></div>
    <div className="mv-float mv-f2"><PrCard /></div>
    <div className="mv-float mv-f3"><Letter title={letter} /></div>
    <div className="mv-float mv-f4">
      <div className="mv-card mv-chip"><RocketLaunchIcon sx={{ fontSize: 16 }} /> Deployed v2.4 to production</div>
    </div>
  </div>
);

// Hero collage for the working professionals page
export const ExpertHeroVisual = () => (
  <div className="mv-collage" aria-hidden="true">
    <div className="mv-float mv-f1">
      <div className="mv-card mv-call">
        <span className="mv-icon mv-cyan"><EventAvailableIcon sx={{ fontSize: 18 }} /></span>
        <div>
          <strong>1:1 call booked</strong>
          <span>30 min · Senior engineer</span>
        </div>
      </div>
      <div className="mv-card mv-plan">
        <strong>Your plan</strong>
        <ul>
          <li className="done"><CheckCircleRoundedIcon sx={{ fontSize: 16 }} /> DSA patterns</li>
          <li className="done"><CheckCircleRoundedIcon sx={{ fontSize: 16 }} /> System design</li>
          <li><CheckCircleRoundedIcon sx={{ fontSize: 16 }} /> Agentic AI project</li>
          <li><CheckCircleRoundedIcon sx={{ fontSize: 16 }} /> CV for product companies</li>
        </ul>
        <span className="mv-progress"><i style={{ width: '50%' }} /></span>
      </div>
    </div>
    <div className="mv-float mv-f2">
      <div className="mv-card mv-chip"><VideocamIcon sx={{ fontSize: 16 }} /> <span className="mv-live-dot" /> System Design · live now</div>
    </div>
    <div className="mv-float mv-f3">
      <div className="mv-card mv-offer-card">
        <span>🎉</span>
        <div>
          <strong>Your next offer</strong>
          <span>Product company · Senior role</span>
        </div>
      </div>
    </div>
  </div>
);

export const WorkshopHeroVisual = () => (
  <div className="mv-collage" aria-hidden="true">
    <div className="mv-float mv-f1">
      <div className="mv-ticket">
        <div className="mv-ticket-main">
          <span className="mv-ticket-kicker"><ConfirmationNumberIcon sx={{ fontSize: 16 }} /> Free live workshop</span>
          <strong>Build your first AI agent</strong>
          <span>2.5 hours · Live online · Q&amp;A</span>
        </div>
        <div className="mv-ticket-stub">
          <span>ADMIT</span>
          <strong>ONE</strong>
          <span>FREE</span>
        </div>
      </div>
      <div className="mv-card mv-chip mv-chip-below"><span className="mv-live-dot" /> Live · ask anything</div>
    </div>
    <div className="mv-float mv-f2 mv-ws-chat"><AiChat /></div>
  </div>
);

// Visuals for feature rows
const featureVisuals = {
  ai: () => <div className="mv-stage"><AiChat /><div className="mv-stage-chip"><PrCard /></div></div>,
  documents: ({ letter }) => (
    <div className="mv-stage mv-docs">
      <Letter title={letter} />
      <Payslip />
    </div>
  ),
  interview: () => (
    <div className="mv-stage">
      <div className="mv-card mv-score">
        <div className="mv-ai-head"><RecordVoiceOverIcon sx={{ fontSize: 16 }} /> Mock interview feedback</div>
        {[['Problem solving', 85], ['Project deep-dive', 90], ['Communication', 75]].map(([label, value]) => (
          <div className="mv-score-row" key={label}>
            <span>{label}</span>
            <span className="mv-progress"><i style={{ width: `${value}%` }} /></span>
          </div>
        ))}
        <p className="mv-note">“Lead with the impact of your checkout fix. Interviewers will love it.”</p>
      </div>
    </div>
  ),
  support: () => (
    <div className="mv-stage">
      <div className="mv-card mv-chat">
        <div className="mv-ai-head"><SupportAgentIcon sx={{ fontSize: 16 }} /> Your mentor</div>
        <p className="mv-bubble mv-bubble-you">Week 2 at the new job. They want me to own a service already 😅</p>
        <p className="mv-bubble mv-bubble-ai">That’s a great sign. Let’s plan your first design doc together on a call tomorrow.</p>
      </div>
    </div>
  ),
  expert: () => (
    <div className="mv-stage">
      <div className="mv-card mv-plan wide">
        <strong>What we cover in 30 minutes</strong>
        <ul>
          <li className="done"><CheckCircleRoundedIcon sx={{ fontSize: 16 }} /> Where you stand today</li>
          <li className="done"><CheckCircleRoundedIcon sx={{ fontSize: 16 }} /> CV review, line by line</li>
          <li className="done"><CheckCircleRoundedIcon sx={{ fontSize: 16 }} /> Your path to the switch</li>
        </ul>
      </div>
      <div className="mv-stage-chip">
        <div className="mv-card mv-call">
          <span className="mv-icon mv-cyan"><EventAvailableIcon sx={{ fontSize: 18 }} /></span>
          <div><strong>Pick a time that suits you</strong><span>Evenings and weekends available</span></div>
        </div>
      </div>
    </div>
  ),
  live: () => (
    <div className="mv-stage">
      <div className="mv-card mv-class">
        <div className="mv-class-head"><span className="mv-live-dot" /> LIVE · System Design</div>
        <div className="mv-class-board">
          <span>Client</span><i>→</i><span>LB</span><i>→</i><span>API</span><i>→</i><span>Cache</span>
        </div>
        <div className="mv-avatars">
          {['A', 'R', 'S', 'K', 'M'].map((l) => <span key={l}>{l}</span>)}
          <em>+ your batch</em>
        </div>
      </div>
    </div>
  ),
  prep: () => (
    <div className="mv-stage">
      <div className="mv-card mv-question">
        <div className="mv-ai-head"><QuestionAnswerIcon sx={{ fontSize: 16 }} /> Interview question · Senior</div>
        <strong>Design a rate limiter for an API.</strong>
        <span className="mv-tag">30-second answer</span>
        <span className="mv-line" />
        <span className="mv-line short" />
        <div className="mv-q-tags"><span>Deep dive</span><span>Follow-ups</span><span>Mistakes</span></div>
      </div>
    </div>
  )
};

export const FeatureVisual = ({ type, ...props }) => {
  const Visual = featureVisuals[type];
  return Visual ? <div className="mv-feature" aria-hidden="true"><Visual {...props} /></div> : null;
};
